import { makeAutoObservable, runInAction } from "mobx"
import { logger } from "../lib/logger"

/**
 * All available classification types
 * Based on the commit types used in the app
 */
export const CLASSIFICATION_TYPES = [
  "feature",
  "bugfix",
  "refactoring",
  "ci",
  "tests",
  "docs",
  "chore",
  "perf",
  "revert",
  "merge",
  "archive",
  "style",
  "other",
  "unclassified",
] as const

export type ClassificationType = (typeof CLASSIFICATION_TYPES)[number]

interface SettingsState {
  selectedClassifications: ClassificationType[]
  selectedTeams: string[]
}

const STORAGE_KEY = "devpulse-settings"

/**
 * Default classifications that should be unchecked
 */
const DEFAULT_UNCHECKED: ClassificationType[] = ["merge", "other", "unclassified"]

/**
 * SettingsStore manages application settings
 * All settings are persisted to localStorage
 */
export class SettingsStore {
  selectedClassifications: Set<ClassificationType>
  selectedTeams: Set<string>
  private availableTeams: string[] = []

  constructor() {
    makeAutoObservable(this)

    // Initialize with all types except defaults unchecked
    this.selectedClassifications = new Set(
      CLASSIFICATION_TYPES.filter((type) => !DEFAULT_UNCHECKED.includes(type)),
    )

    // Initialize with empty set - will be populated when teams are loaded
    this.selectedTeams = new Set<string>()

    // Load from localStorage
    this.loadFromStorage()
  }

  /**
   * Initialize available teams from metadata
   * This should be called after MetadataStore is initialized
   */
  initializeTeams(teams: string[]): void {
    runInAction(() => {
      this.availableTeams = teams

      // Load stored selection
      const stored = this.loadTeamsFromStorage()

      if (stored !== null && stored.length > 0) {
        // Use stored selection, but only keep teams that still exist
        const validTeams = stored.filter((t) => teams.includes(t))
        // If all stored teams are now invalid, default to all teams selected
        this.selectedTeams = new Set(validTeams.length > 0 ? validTeams : teams)
      } else {
        // Default: all teams selected
        this.selectedTeams = new Set(teams)
      }

      logger.debug("Teams initialized in SettingsStore", {
        component: "SettingsStore",
        availableTeams: teams.length,
        selectedTeams: this.selectedTeams.size,
      })
    })
  }

  /**
   * Toggle a classification on/off
   */
  toggleClassification(type: ClassificationType): void {
    runInAction(() => {
      if (this.selectedClassifications.has(type)) {
        // Create new Set without the type to trigger reactivity
        this.selectedClassifications = new Set(
          Array.from(this.selectedClassifications).filter((t) => t !== type),
        )
      } else {
        // Create new Set with the type to trigger reactivity
        this.selectedClassifications = new Set([...this.selectedClassifications, type])
      }
      this.saveToStorage()
    })
  }

  /**
   * Check if a classification is selected
   */
  isClassificationSelected(type: ClassificationType): boolean {
    return this.selectedClassifications.has(type)
  }

  /**
   * Get all selected classifications as an array
   */
  get selectedClassificationsArray(): ClassificationType[] {
    return Array.from(this.selectedClassifications)
  }

  /**
   * Toggle a team on/off
   */
  toggleTeam(team: string): void {
    runInAction(() => {
      if (this.selectedTeams.has(team)) {
        // Create new Set without the team to trigger reactivity
        this.selectedTeams = new Set(Array.from(this.selectedTeams).filter((t) => t !== team))
      } else {
        // Create new Set with the team to trigger reactivity
        this.selectedTeams = new Set([...this.selectedTeams, team])
      }
      this.saveToStorage()
    })
  }

  /**
   * Check if a team is selected
   */
  isTeamSelected(team: string): boolean {
    return this.selectedTeams.has(team)
  }

  /**
   * Get all selected teams as an array
   */
  get selectedTeamsArray(): string[] {
    return Array.from(this.selectedTeams)
  }

  /**
   * Get all available teams
   */
  get allAvailableTeams(): string[] {
    return this.availableTeams
  }

  /**
   * Select all teams
   */
  selectAllTeams(): void {
    runInAction(() => {
      this.selectedTeams = new Set(this.availableTeams)
      this.saveToStorage()
    })
  }

  /**
   * Deselect all teams
   */
  deselectAllTeams(): void {
    runInAction(() => {
      this.selectedTeams = new Set()
      this.saveToStorage()
    })
  }

  /**
   * Save settings to localStorage
   */
  private saveToStorage(): void {
    try {
      const state: SettingsState = {
        selectedClassifications: this.selectedClassificationsArray,
        selectedTeams: this.selectedTeamsArray,
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
      logger.debug("Settings saved to localStorage", {
        component: "SettingsStore",
        selectedClassifications: this.selectedClassifications.size,
        selectedTeams: this.selectedTeams.size,
      })
    } catch (error) {
      logger.error("Failed to save settings to localStorage", error, {
        component: "SettingsStore",
      })
    }
  }

  /**
   * Load teams selection from localStorage (helper for initialization)
   */
  private loadTeamsFromStorage(): string[] | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const state: SettingsState = JSON.parse(stored)
        return state.selectedTeams || null
      }
    } catch (error) {
      logger.error("Failed to load teams from localStorage", error, {
        component: "SettingsStore",
      })
    }
    return null
  }

  /**
   * Load settings from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const state: SettingsState = JSON.parse(stored)
        this.selectedClassifications = new Set(state.selectedClassifications)
        // Teams will be loaded separately via initializeTeams
        logger.debug("Settings loaded from localStorage", {
          component: "SettingsStore",
          selectedCount: this.selectedClassifications.size,
        })
      }
    } catch (error) {
      logger.error("Failed to load settings from localStorage", error, {
        component: "SettingsStore",
      })
    }
  }

  /**
   * Reset to default settings
   */
  reset(): void {
    this.selectedClassifications = new Set(
      CLASSIFICATION_TYPES.filter((type) => !DEFAULT_UNCHECKED.includes(type)),
    )
    this.selectedTeams = new Set(this.availableTeams)
    this.saveToStorage()
  }
}
